// Navigation principale par rôle (maquettes haute fidélité, `NAVS` de hf-lib.js). Chaque entrée porte sa route et,
// si besoin, sa permission. Une entrée sans route correspond à un module pas encore livré : elle est masquée, et la
// navigation s'enrichit lot par lot. Animateur et Membre n'ont pas de navigation web dans les maquettes : navigation
// déduite de la matrice des droits (écart signalé au lot F0 web).
import {
  BadgeCheck,
  Banknote,
  BellRing,
  BookOpen,
  Briefcase,
  Calendar,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  CircleCheck,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileBarChart,
  FileText,
  Flag,
  FolderKanban,
  FolderOpen,
  GraduationCap,
  Group,
  HandCoins,
  House,
  IdCard,
  Landmark,
  LayoutDashboard,
  Library,
  ListTodo,
  Megaphone,
  MessageSquare,
  Network,
  PenLine,
  Plug,
  Receipt,
  ScrollText,
  Settings,
  Shield,
  ShoppingCart,
  Table,
  Trash2,
  TrendingUp,
  type LucideIcon,
  User,
  UserCheck,
  UserPen,
  Users,
  UsersRound,
  Video,
  Wallet,
} from 'lucide-react';
import type { Me } from '@/app/me';
import { can, canGlobally, PERMISSIONS, type Permission } from '@/lib/permissions';

export interface NavItem {
  id: string;
  /** Clé i18n dans l'espace `nav.items`. */
  label: string;
  icon: LucideIcon;
  /** Route livrée ; absente = module pas encore disponible (entrée masquée). */
  to?: string;
  permission?: Permission;
  /** Page globale : la permission doit porter sur « tous » (ROL-06), pas seulement sur ses données. */
  global?: boolean;
  /** Compteur affiché à droite (« comms » : messages non lus). */
  badge?: 'comms';
  /** Autres pages de la section, pour l'entrée active (achats : factures, fournisseurs, circuits). */
  sections?: string[];
  /** Paramètres d'URL de la page (lot F14d : « Attestations » → onglet Terminées d'ACA-P13). */
  search?: Record<string, string>;
}

export interface NavGroup {
  /** Clé i18n dans `nav.groups`, ou null pour le groupe principal sans titre. */
  label: string | null;
  items: NavItem[];
}

const item = (id: string, icon: LucideIcon, extra: Partial<NavItem> = {}): NavItem => ({ id, label: id, icon, ...extra });

/** Routes livrées (F0 web à F4 web). */
export const ROUTES = {
  home: '/',
  mySpace: '/moi',
  notifications: '/notifications',
  notificationPreferences: '/moi/notifications',
  alertRules: '/admin/alertes',
  search: '/recherche',
  portfolio: '/tableaux-de-bord/projets',
  myStatistics: '/tableaux-de-bord/mes-statistiques',
  statistics: '/tableaux-de-bord/statistiques',
  profile: '/moi/profil',
  security: '/moi/securite',
  myActivity: '/moi/activite',
  preferences: '/moi/preferences',
  myExports: '/moi/exports',
  personalData: '/moi/donnees-personnelles',
  settings: '/admin/parametres',
  rectifications: '/admin/rectifications',
  templates: '/admin/modeles',
  integrations: '/admin/integrations',
  trash: '/admin/corbeille',
  fullExport: '/admin/export-complet',
  imports: '/imports',
  users: '/admin/utilisateurs',
  userCreate: '/admin/utilisateurs/nouveau',
  userImport: '/admin/utilisateurs/import',
  roles: '/admin/roles',
  viewAs: '/admin/voir-en-tant-que',
  audit: '/admin/audit',
  auditStats: '/admin/audit/statistiques',
  referentials: '/referentiels',
  localities: '/referentiels/localites',
  localityImport: '/referentiels/localites/import',
  partners: '/referentiels/partenaires',
  donors: '/referentiels/bailleurs',
  departments: '/referentiels/departements',
  lists: '/referentiels/listes',
  projects: '/projets',
  myProjects: '/mes-projets',
  projectCreate: '/projets/nouveau',
  projectImport: '/projets/import',
  activities: '/activites',
  myActivities: '/mes-activites',
  planning: '/planning',
  milestones: '/jalons',
  deliverablesReview: '/livrables-a-valider',
  collections: '/collectes',
  collectionsQueue: '/collectes/a-valider',
  forms: '/admin/formulaires',
  budgets: '/budgets',
  expenses: '/depenses',
  expensesQueue: '/depenses/a-valider',
  reports: '/rapports',
  myReports: '/mes-rapports',
  reportsQueue: '/rapports/a-valider',
  analyses: '/analyses',
  messaging: '/messagerie',
  announcements: '/annonces',
  staff: '/rh/personnel',
  orgChart: '/rh/organigramme',
  directory: '/rh/annuaire',
  leaveQueue: '/rh/conges-a-traiter',
  myLeaves: '/rh/mes-conges',
  myFile: '/rh/mon-dossier',
  payroll: '/rh/paie',
  myPayslips: '/rh/mes-bulletins',
  evaluations: '/rh/evaluations',
  myEvaluations: '/rh/mes-evaluations',
  recruitment: '/rh/recrutement',
  recruitmentApprovals: '/rh/recrutements-a-approuver',
  hrDashboard: '/rh/tableau-de-bord',
  members: '/association/membres',
  membershipRequests: '/association/demandes',
  memberDirectory: '/association/annuaire',
  myMemberCard: '/mon-adhesion/carte',
  entries: '/finance/ecritures',
  entriesQueue: '/finance/a-valider',
  ledger: '/finance/grand-livre',
  purchaseOrders: '/finance/bons-de-commande',
  payments: '/finance/paiements',
  reconciliation: '/finance/rapprochement',
  financialStatements: '/finance/etats-financiers',
  financialDashboard: '/finance/tableau-de-bord',
} as const;

const me = item('me', User, { to: ROUTES.mySpace });
const projects = item('projects', FolderKanban, { to: ROUTES.projects, permission: PERMISSIONS.projectsView });
// PRJ-P02 : chef de projet, superviseur et animateur arrivent sur leurs affectations (PRJ-P01 « Mes projets » par défaut).
const myProjects = item('my_projects', FolderKanban, { to: ROUTES.myProjects, permission: PERMISSIONS.projectsView });
// Lot F4 (décision C) : chef de projet et superviseur « Activités » → ACT-P01 (lien vers « Mes activités ») ; animateur
// → ACT-P02 ; Direction « Activités et planning » → ACT-P01 et « Validations » → JAL-P03 (provisoire, jusqu'au lot F7).
const activities = item('activities', ListTodo, { to: ROUTES.activities, permission: PERMISSIONS.activitiesView });
const myActivities = item('activities', ListTodo, { to: ROUTES.myActivities, permission: PERMISSIONS.activitiesView });
const milestones = item('milestones', Flag, { to: ROUTES.milestones, permission: PERMISSIONS.activitiesView });
// Lot F5 : « Collectes » → COL-P10 ; superviseur « À valider » → COL-P08 ; administration « Formulaires » → COL-P13.
const collections = item('collections', ClipboardList, { to: ROUTES.collections, permission: PERMISSIONS.fieldView });
// Lot F6 : « Budgets » → BUD-P01 ; chef de projet « Budget et dépenses » et comptable « Dépenses projet » → BUD-P03 ;
// comptable « À valider » → BUD-P06 (accueil comptable HOME-P05 au lot F7).
// Lot F11c-1 : section « Budgets » (onglets : budget institutionnel FIN-P13, consolidation FIN-P14, virements FIN-P15).
const budgets = item('budgets', Wallet, { to: ROUTES.budgets, permission: PERMISSIONS.budgetView, sections: ['/finance/budget-institutionnel', '/finance/budgets-consolides', '/finance/virements'] });
// Lot F8 : « Messagerie » → COM-P01, pastille des non lus (COM-07).
const messaging = item('messaging', MessageSquare, { to: ROUTES.messaging, permission: 'comms.view', badge: 'comms' });
// Lot F16b : espace d'équipe (COM-P05 à COM-P07), après la messagerie (la BottomNav garde ses cinq premières entrées).
const team = item('team', Group, { to: '/equipe', permission: 'comms.view', sections: ['/equipe'] });
// Lot F16d : visioconférence (salles permanentes COM-P09, enregistrements COM-P10 ; salle COM-P08 en plein écran).
const video = item('video', Video, { to: '/visio/salles', permission: 'comms.view', sections: ['/visio'] });
// Lot F7a : accueil par rôle (HOME-P01 à P10) ; tableaux de bord → DSH-P01 (permission « Tableaux de bord »).
const home = item('home', House, { to: ROUTES.home });
const dashboards = item('dashboards', LayoutDashboard, { to: ROUTES.portfolio, permission: 'dashboards.view' });
// Lot F7b : « Rapports » → RAP-P01 (visibilité RAP-05 côté serveur) ; Direction « Analyses » → ANA-P01.
const reports = item('reports', FileText, { to: ROUTES.reports, permission: PERMISSIONS.reportsView });
const analyses = item('analyses', ChartColumn, { to: ROUTES.analyses, permission: PERMISSIONS.dashboardsView });
// Lot F10a : « Ressources humaines » (Administrateur, Direction) et « Personnel » (RH) → RH-P01 ; « Organigramme » →
// RH-P05 ; « Congés et présences » → RH-P08 (onglets calendrier, planning, paramètres).
const hr = item('hr', UsersRound, { to: ROUTES.staff, permission: 'hr.records.view' });
// Lot F10b : « Paie » → RH-W02 (RH : préparation ; Direction : validation, ARB-34 ; Comptable : lecture et paiement).
const payroll = item('payroll', Banknote, { to: ROUTES.payroll, permission: 'hr.payroll.view' });
// Lot F10c : RH « Évaluations » → RH-P17, « Recrutement » → RH-P20, « Tableau de bord RH » → RH-P25 ; Direction
// « Tableau de bord RH » (lecture) et « Recrutements à approuver » → RH-P24 (RH-27).
const hrDashboard = item('hr_dashboard', LayoutDashboard, { to: ROUTES.hrDashboard, permission: 'hr.dashboard.view' });
const recruitmentApprovals = item('recruitment_approvals', UserCheck, { to: ROUTES.recruitmentApprovals, permission: 'hr.recruitment.approve' });
// Lot F11a : « Finance » (Administrateur, Direction) et comptable « Écritures » → FIN-P04 (onglets : file de validation,
// paramétrage, export) ; « Consultations » → FIN-P08 (grand livre, balance, pièces). Lecture de toute la comptabilité.
// Lot F11c-2 : clôture (FIN-W01, depuis les exercices) et états financiers (FIN-P12, onglet des consultations).
const finance = item('finance', Landmark, { to: ROUTES.entries, permission: 'finance.view', global: true, sections: ['/finance'] });
// Lot F11b-1 : « Achats et fournisseurs » → FIN-P19 (onglets : factures, fournisseurs, circuits) ; « Paiements et caisse »
// → FIN-P24. Comptable : gestion ; Administrateur et Direction : lecture et approbation (décision O).
const purchases = item('purchases', ShoppingCart, {
  to: ROUTES.purchaseOrders,
  permission: 'finance.view',
  global: true,
  sections: ['/finance/factures', '/finance/fournisseurs', '/finance/circuits'],
});
const payments = item('payments', Banknote, { to: ROUTES.payments, permission: 'finance.view', global: true, sections: ['/finance/caisse'] });
// Lot F11b-2 : « Trésorerie » → FIN-P11 (onglets : rapprochement bancaire, lettrage).
const treasury = item('treasury', TrendingUp, { to: ROUTES.reconciliation, permission: 'finance.view', global: true, sections: ['/finance/lettrage', '/finance/previsions'] });
// Lot F12a : « Vie associative » → ASSO-P02 (onglets : demandes d'adhésion ASSO-P01, annuaire ASSO-P04) pour la gestion,
// la Direction et le Comptable (lecture), section active sur tout /association (F12b : cotisations) ; Membre : « Carte » → ASSO-P05 et « Annuaire » → ASSO-P04 (ARB-40).
// Lot F12d : la section s'ouvre sur le tableau de bord (ASSO-P18, entrée « Sidebar »).
const association = item('association', IdCard, { to: '/association/tableau-de-bord', permission: 'association.view', global: true, sections: ['/association'] });

// Lot F14a : « Académie » (Administrateur, Direction) → ACA-P02, section active sur tout /academie ; formateur « Mes
// formations » → ACA-P02 (ses formations) et « Sessions » → ACA-P05 ; membre, apprenant externe et animateur
// « Académie » → catalogue interne ACA-P01. « Corrections » et « Émargement » : lot F14c.
const academy = item('academy', GraduationCap, { to: '/academie/gestion', permission: 'academy.view', global: true, sections: ['/academie'] });
const academyCatalog = item('academy', GraduationCap, { to: '/academie', permission: 'academy.view', sections: ['/academie'] });
// Lot F15a : « Capitalisation » → bibliothèque CAP-P01 (Direction et chef de projet dans les maquettes ; Administrateur et
// Membre : déduits de la matrice), section active sur tout /capitalisation.
const knowledge = item('knowledge', Library, { to: '/capitalisation', permission: 'knowledge.view', sections: ['/capitalisation'] });

const NAV_BY_ROLE: Record<string, NavGroup[]> = {
  admin: [
    {
      label: 'administration',
      items: [
        home,
        item('users', Users, { to: ROUTES.users, permission: PERMISSIONS.accountsView }),
        item('roles', Shield, { to: ROUTES.roles, permission: PERMISSIONS.accountsAdminister }),
        item('audit', ScrollText, { to: ROUTES.audit, permission: PERMISSIONS.auditView, global: true }),
        item('referentials', Database, { to: ROUTES.referentials, permission: PERMISSIONS.referentialsView }),
        item('forms', ClipboardList, { to: ROUTES.forms, permission: PERMISSIONS.fieldDelete, global: true }),
        item('integrations', Plug, { to: ROUTES.integrations, permission: PERMISSIONS.integrationsView }),
        item('alert_rules', BellRing, { to: ROUTES.alertRules, permission: 'alerts.rules.manage' }),
        item('settings', Settings, { to: ROUTES.settings, permission: PERMISSIONS.settingsManage }),
        // TRV-10 (lot D3) : demandes de rectification des comptes (TRV-P07).
        item('rectifications', UserPen, { to: ROUTES.rectifications, permission: 'accounts.update' }),
        item('trash', Trash2, { to: ROUTES.trash, permission: PERMISSIONS.trashManage }),
      ],
    },
    { label: 'modules', items: [projects, budgets, dashboards, analyses, reports, finance, purchases, hr, payroll, association, academy, knowledge, messaging, team, video] },
  ],
  director: [
    {
      label: null,
      items: [
        home,
        item('validations', CircleCheck, { to: ROUTES.deliverablesReview, permission: PERMISSIONS.activitiesValidate }),
        projects,
        item('activities_planning', CalendarRange, { to: ROUTES.activities, permission: PERMISSIONS.activitiesView, global: true }),
        budgets,
        dashboards,
        analyses,
        reports,
      ],
    },
    { label: 'support', items: [{ ...hr, icon: Users }, payroll, recruitmentApprovals, hrDashboard, finance, purchases] },
    { label: 'community', items: [association, academy, knowledge, messaging, team, video] },
    // Écarts F1-W01 et F2-W01 : la matrice donne à la Direction la lecture des comptes, du journal et des référentiels,
    // absents de sa navigation. Les autres profils internes lisent les référentiels depuis les liens des modules.
    {
      label: 'administration',
      items: [
        item('users', Users, { to: ROUTES.users, permission: PERMISSIONS.accountsView }),
        item('audit', ScrollText, { to: ROUTES.audit, permission: PERMISSIONS.auditView, global: true }),
        item('referentials', Database, { to: ROUTES.referentials, permission: PERMISSIONS.referentialsView }),
      ],
    },
  ],
  project_manager: [
    {
      label: null,
      items: [
        home,
        myProjects,
        activities,
        milestones,
        collections,
        item('budget_expenses', Wallet, { to: ROUTES.expenses, permission: PERMISSIONS.budgetView }),
        reports,
        dashboards,
        knowledge,
        messaging, team, video,
        me,
      ],
    },
  ],
  supervisor: [
    {
      label: null,
      items: [home, item('to_validate', CircleCheck, { to: ROUTES.collectionsQueue, permission: PERMISSIONS.fieldValidate }), myProjects, activities, collections, reports, { ...dashboards, id: 'dashboard', label: 'dashboard' }, messaging, team, video, me],
    },
  ],
  accountant: [
    {
      label: null,
      items: [
        home,
        item('to_validate', CircleCheck, { to: ROUTES.expensesQueue, permission: PERMISSIONS.budgetValidate }),
        item('entries', BookOpen, { to: ROUTES.entries, permission: 'finance.view', global: true }),
        item('consultations', Table, { to: ROUTES.ledger, permission: 'finance.view', global: true }),
        budgets,
        purchases,
        payments,
        treasury,
        // Lot F11c-3 : « États et rapports » → FIN-P27 (onglets : états financiers FIN-P12, rapport bailleur FIN-W02, modèles FIN-P28).
        item('financial_reports', FileBarChart, { to: ROUTES.financialDashboard, permission: 'finance.view', global: true, sections: [ROUTES.financialStatements, '/finance/modeles-bailleurs', '/finance/rapport-bailleur'] }),
        item('project_expenses', Receipt, { to: ROUTES.expenses, permission: PERMISSIONS.budgetView }),
        { ...payroll, icon: HandCoins },
        association,
        messaging, team, video,
        me,
      ],
    },
  ],
  hr_manager: [
    {
      label: null,
      items: [home, item('staff', Users, { to: ROUTES.staff, permission: 'hr.view' }), item('org_chart', Network, { to: ROUTES.orgChart }), item('leaves', CalendarDays, { to: ROUTES.leaveQueue }), payroll, item('evaluations', ClipboardCheck, { to: ROUTES.evaluations, permission: 'hr.evaluations.manage' }), item('recruitment', Briefcase, { to: ROUTES.recruitment, permission: 'hr.recruitment.manage' }), hrDashboard, item('rectifications', UserPen, { to: ROUTES.rectifications, permission: 'hr.update' }), messaging, team, video, me],
    },
  ],
  trainer: [
    {
      label: null,
      items: [
        home,
        item('my_courses', GraduationCap, { to: '/academie/gestion', permission: 'academy.update', sections: ['/academie/gestion', '/academie/modules'] }),
        item('sessions', Calendar, { to: '/academie/sessions', permission: 'academy.update' }),
        // Lot F14c : corrections (ACA-P10) et émargement (ACA-P11).
        item('grading', PenLine, { to: '/academie/corrections', permission: 'academy.update' }),
        item('attendance', ClipboardCheck, { to: '/academie/emargement', permission: 'academy.update' }),
        messaging, team, video,
        me,
      ],
    },
  ],
  // Déduites de la matrice des droits (pas de navigation web dans les maquettes).
  facilitator: [{ label: null, items: [home, myProjects, myActivities, collections, academyCatalog, messaging, team, video, me] }],
  member: [
    {
      label: null,
      items: [
        home,
        item('my_card', IdCard, { to: ROUTES.myMemberCard }),
        // Lot F12c : réunions où le membre est convoqué (ASSO-P12, ASSO-P13, vote ASSO-P15).
        item('member_meetings', Calendar, { to: '/association/reunions' }),
        // Lot F12d : annonces aux membres (ASSO-P20) et espace documentaire (ASSO-P21).
        item('member_announcements', Megaphone, { to: '/association/annonces' }),
        item('member_documents', FolderOpen, { to: '/association/documents' }),
        item('member_directory', UsersRound, { to: ROUTES.memberDirectory }),
        academyCatalog,
        { ...knowledge, icon: BadgeCheck },
        messaging, team, video,
        me,
      ],
    },
  ],
  // Apprenant externe (ARB-12, maquettes HOME-P09 / ACA-P13) : « Mes formations » (accueil HOME-P09 et ACA-P13, lot F14b),
  // « Catalogue » (ACA-P01), « Attestations » (ACA-P13 Terminées, lot F14d), « Profil ».
  external_learner: [
    {
      label: null,
      items: [
        item('learner_home', GraduationCap, { to: ROUTES.home, sections: ['/academie/mes-formations'] }),
        item('catalog', Library, { to: '/academie', permission: 'academy.view', sections: ['/academie/formations'] }),
        // Lot F14d : attestations (onglet Terminées d'ACA-P13).
        item('certificates', BadgeCheck, { to: '/academie/mes-formations', search: { onglet: 'terminees' } }),
        item('profile', User, { to: ROUTES.mySpace }),
      ],
    },
  ],
};

/** Groupes visibles pour l'utilisateur : rôle principal, entrées livrées et autorisées uniquement. */
export function navigationFor(user: Pick<Me, 'primary_role' | 'permissions' | 'permission_scopes'>): NavGroup[] {
  const role = user.primary_role?.code ?? 'facilitator';
  const groups = NAV_BY_ROLE[role] ?? NAV_BY_ROLE.facilitator ?? [];
  return groups
    .map((group) => ({ ...group, items: group.items.filter((entry) => entry.to && (!entry.permission || (entry.global ? canGlobally(user, entry.permission) : can(user, entry.permission)))) }))
    .filter((group) => group.items.length > 0);
}

/** Rôles affichés avec une BottomNav sur mobile (Lot 1b : Animateur, Membre, Apprenant). */
export const BOTTOM_NAV_ROLES = ['facilitator', 'member', 'external_learner'];
